import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  MenuItem,
  Typography,
  Select,
  Tooltip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { SegmentStatusIcon } from '../SegmentStatusIcon';
import { useTypedSelector } from 'combineReducers';
import { ISegmentStatus, SegmentStatusIcons } from 'interfaces/segmentStatuses';
import { LOGOS } from 'helpers/newDashboard';
import { ISectionNav } from 'interfaces/sections';
import { QueryBuilder } from '@pec/aion-ui-odata/builders/odataQueryBuilder';
import { OdataComparator } from '@pec/aion-ui-odata/types/odataComparator';
import { uniq, uniqBy } from 'lodash';
import axios from 'axios';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { CircularProgress } from '@pec/aion-ui-components/components/CircularProgress';
const { In } = OdataComparator;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuTitle: {
      display: 'flex',
      gap: `${theme.spacing(2)}px`,
      padding: `${theme.spacing(1.5)}px ${theme.spacing(0.5)}px`
    },
    title: {
      fontSize: 16,
      lineHeight: '24px',
      color: 'var(--on-surface-medium-emphasis-000000-87-)',
      padding: theme.spacing(2),
      borderBottom: '1px solid var(--on-surface-outline-000000-12-)'
    },
    select: {
      marginBottom: theme.spacing(2)
    },
    actionRequiredIcon: {
      marginLeft: theme.spacing(1)
    },
    logo: {
      width: 40,
      height: 40,
      marginRight: 8
    },
    progressValue: {
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: '24px',
      textAlign: 'center'
    },
    progressBar: {
      height: 4,
      marginBottom: theme.spacing(2)
    },
    progressColor: {
      backgroundColor: '#99c0ca'
    },
    progressBarColor: {
      borderRadius: 4
    },
    tableWrapper: {
      maxHeight: 300,
      overflowY: 'auto',
      '& th': {
        backgroundColor: '#fff'
      }
    }
  })
);

const actionRequiredStatuses = [
  SegmentStatusIcons.AdditionalInformationRequested,
  SegmentStatusIcons.ContractorActionRequired,
  SegmentStatusIcons.DocumentExpired,
  SegmentStatusIcons.ReAssessmentRequired
];

interface ISubscription {
  id: string;
  legacySubscriptionTypeId: number;
  description: string;
  altDescription: string | null;
  countryISO2: string;
  isDeleted: boolean;
  isMain: boolean;
}

type ModuleListItem = {
  module: {
    id: string;
    moduleId?: string | null;
    moduleTitle: string | null;
    legacySubscriptionTypeId?: string | null;
  };
  status?: string;
};

type Props = {
  statusSegmentSections: Pick<{}, never>;
};
export const QuestionnaireCHAS: React.FC<Props> = ({ statusSegmentSections }) => {
  const { t } = useTranslation('subscriber');
  const classes = useStyles();
  const { organizationId } = useParams<{
    organizationId: string;
  }>();
  const [activeSubscription, setActiveSubscription] = React.useState('');
  const [subscriptionsWithName, setSubscriptionsWithName] = React.useState<ISubscription[]>([]);
  const [isfetchingSubscription, setIsFetchingSubscription] = React.useState(true);

  const { subscriptions } = useTypedSelector(state => state.subscriptions);
  const { segmentStatuses } = useTypedSelector(state => state.segmentStatuses);
  const { sections } = useTypedSelector(state => state.sections);

  // Map for status ordering (from segmentStatuses)
  const statusPriorityMap = React.useMemo(() => {
    const map = new Map<string, number>();
    segmentStatuses.forEach(status => {
      if (!map.has(status.contractorStatus)) {
        map.set(status.contractorStatus, status.priority);
      }
    });
    return map;
  }, [segmentStatuses]);

  // Map for module ordering (from sections/sectionOrders)
  const moduleDisplayOrderMap = React.useMemo(() => {
    const map = new Map<string, number>();
    sections.forEach(section => {
      if (section.moduleId && section.moduleDisplayOrder !== undefined && !map.has(section.moduleId)) {
        map.set(section.moduleId, section.moduleDisplayOrder);
      }
    });
    return map;
  }, [sections]);

  // Compare function for status groups
  const statusCompareFn = (a: [string, unknown], b: [string, unknown]) => {
    if (a[0] === 'null') return 1;
    if (b[0] === 'null') return -1;

    const priorityA = statusPriorityMap.get(a[0]) ?? Number.MAX_SAFE_INTEGER;
    const priorityB = statusPriorityMap.get(b[0]) ?? Number.MAX_SAFE_INTEGER;

    return priorityA - priorityB;
  };

  // Compare function for modules within status
  const moduleCompareFn = (a: [string, ISectionNav[]], b: [string, ISectionNav[]]) => {
    if (a[0] === 'null') return 1;
    if (b[0] === 'null') return -1;

    const orderA = moduleDisplayOrderMap.get(a[0]) ?? Number.MAX_SAFE_INTEGER;
    const orderB = moduleDisplayOrderMap.get(b[0]) ?? Number.MAX_SAFE_INTEGER;

    return orderA - orderB;
  };

  const modules: ModuleListItem[] = Object.entries(statusSegmentSections)
    .sort(statusCompareFn)
    .flatMap(([status, modulesByKey]) =>
      Object.entries(modulesByKey as Record<string, ISectionNav[]>)
        .sort(moduleCompareFn)
        .map(([, moduleSections]) => ({
          module: moduleSections[0],
          status
        }))
    )
    .filter(module => module.module.legacySubscriptionTypeId === activeSubscription);
  const otherModules: ModuleListItem[] = uniqBy(
    sections
      .filter(section => !segmentStatuses.find(status => status.segmentId === section.moduleId))
      .map(section => ({ module: { id: section.moduleId || '', moduleTitle: section.moduleTitle || '' } })),
    'module.id'
  );

  const handleSubscriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setActiveSubscription(value.toString());
  };

  const isDocumentExpiring = (status: SegmentStatusIcons, documentExpired?: string): boolean =>
    documentExpired ? documentExpired === 'Soon' && status === SegmentStatusIcons.Compliant : false;

  const showActionIcon = segmentStatuses.some(
    ({ documentExpired, contractorStatus }) =>
      actionRequiredStatuses.includes(contractorStatus) || isDocumentExpiring(contractorStatus, documentExpired)
  );

  const getShowActionRequiredIcon = (segmentStatuses: ISegmentStatus[], subscriptionTypeId: number): boolean =>
    segmentStatuses.some(({ legacySubscriptionTypeId, contractorStatus, documentExpired }) => {
      return (
        legacySubscriptionTypeId === subscriptionTypeId &&
        (actionRequiredStatuses.includes(contractorStatus) || isDocumentExpiring(contractorStatus, documentExpired))
      );
    });

  const hasSubscriptions = subscriptions && subscriptions.length > 0;
  const hasOtherOption =
    hasSubscriptions &&
    sections.some(section => !segmentStatuses.some(status => status.segmentId === section.moduleId));

  const getSubscriptionTypes = async (subscriptionTypeIds: string[]) => {
    const params = new QueryBuilder()
      .filter(f => f.filterBy('legacySubscriptionTypeId', In, uniq(subscriptionTypeIds)))
      .toQueryParam();
    const { data } = await axios.get<OdataResponse<ISubscription[]>>('/api/v3/subscriptionTypes', { params });
    setSubscriptionsWithName(data.value);
    const mainSubscription = data.value.find(sub => sub.isMain)?.legacySubscriptionTypeId.toString();
    setActiveSubscription(mainSubscription || data.value[0]?.legacySubscriptionTypeId.toString());

    setIsFetchingSubscription(false);
  };

  React.useEffect(() => {
    const subscriptionTypeIds = segmentStatuses
      .map(status => status.legacySubscriptionTypeId?.toString() ?? '')
      .filter(subscriptionTypeId => subscriptionTypeId !== '');

    if (subscriptionTypeIds) {
      getSubscriptionTypes(subscriptionTypeIds);
    } else {
      setIsFetchingSubscription(false);
    }
  }, [segmentStatuses]);

  if (isfetchingSubscription)
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  if (modules.length === 0 && subscriptionsWithName.length <= 1) return null;

  return (
    <>
      {subscriptionsWithName.length > 1 && (
        <Select
          fullWidth
          variant="filled"
          value={activeSubscription}
          classes={{ root: classes.title }}
          className={classes.select}
          onChange={handleSubscriptionChange}
          renderValue={(value: string) => {
            return (
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  {subscriptionsWithName.find(
                    subscription => value === subscription.legacySubscriptionTypeId.toString()
                  )?.description || value}
                </Box>
                {showActionIcon && (
                  <Tooltip
                    title={
                      t(
                        'newDashboard.questionnaire.needActionIconTooltip',
                        'Other question sets require attention'
                      ) as string
                    }
                  >
                    <ReportProblemOutlinedIcon color="primary" />
                  </Tooltip>
                )}
              </Box>
            );
          }}
        >
          {subscriptionsWithName.map(subscription => {
            const Logo = LOGOS[subscription.legacySubscriptionTypeId];
            return (
              <MenuItem key={subscription.id} value={subscription.legacySubscriptionTypeId}>
                {Logo && <Logo className={classes.logo} />}
                {subscription.description}
                {getShowActionRequiredIcon(segmentStatuses, subscription.legacySubscriptionTypeId) && (
                  <ReportProblemOutlinedIcon fontSize="small" classes={{ root: classes.actionRequiredIcon }} />
                )}
              </MenuItem>
            );
          })}
          {hasOtherOption && <MenuItem value={'Other'}>{t('common.other', 'Other')}</MenuItem>}
        </Select>
      )}
      <Box className={classes.tableWrapper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('newDashboard.questionnaire.module', 'Module')}</TableCell>
              <TableCell>{t('newDashboard.questionnaire.status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(activeSubscription !== 'Other' ? modules : otherModules).map(({ module, status }) => (
              <TableRow key={module.id}>
                <TableCell>
                  <Link color="primary" href={`/organizations/${organizationId}/questionnaire-v2/${module.id}`}>
                    {module.moduleTitle}
                  </Link>
                </TableCell>
                <TableCell>
                  {status && (
                    <Box display="flex" alignItems="center">
                      <SegmentStatusIcon segmentStatus={status} />
                      <Typography variant="body2" style={{ marginLeft: 8 }}>
                        {status}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
